import { AccordionDetails, Button, Grid, IconButton, Typography } from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorefrontFAQData } from '../../../api';
import { Accordion, AccordionSummary } from '../../../calendar/dialogs/EditOrderDialog/components/Accordion';
import { useLanguage } from '../../../common/GeneralUtils';
import { StorefrontPanels } from '../StorefrontDrawer';
import { FAQEditDialog } from './FAQEditDialog';

type FAQsPanelProps = {
  faqs: StorefrontFAQData[];
  onChange: (faqs: StorefrontFAQData[]) => void;
  expanded?: StorefrontPanels;
  onPanelChange: (panel: StorefrontPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  panelName: StorefrontPanels;
};

export const FAQsPanel = ({ faqs, onChange, expanded, onPanelChange, panelName }: FAQsPanelProps) => {
  const { t } = useTranslation();
  const language = useLanguage();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingIndex(null);
  };

  const handleSave = (faq: StorefrontFAQData) => {
    if (editingIndex === null) {
      onChange([...faqs, faq]);
    } else {
      const updated = [...faqs];
      updated[editingIndex] = faq;
      onChange(updated);
    }
  };

  const handleDelete = (index: number) => {
    onChange(faqs.filter((_, i) => i !== index));
  };

  const editingFaq = editingIndex !== null ? faqs[editingIndex] : null;

  return (
    <>
      <Accordion expandedPanelName={expanded} panelName={panelName} onChange={onPanelChange}>
        <AccordionSummary
          title={t('storefronts.faqs')}
          summaryComponent={<Typography variant="body2">{faqs.length}</Typography>}
        />
        <AccordionDetails>
          <Grid container flexDirection="column" gap={2}>
            {faqs.map((faq, index) => (
              <Grid
                key={index}
                container
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '6px' }}>
                <Grid item xs={9}>
                  <Typography fontWeight="600">
                    {faq.question?.[language] || faq.question?.en || faq.question?.he}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {faq.answer?.[language] || faq.answer?.en || faq.answer?.he}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton size="small" onClick={() => handleEdit(index)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            {faqs.length === 0 && (
              <Typography color="text.secondary" variant="body2">
                {t('storefronts.no_faqs')}
              </Typography>
            )}

            <Button startIcon={<Add />} onClick={handleAdd}>
              {t('storefronts.add_faq')}
            </Button>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <FAQEditDialog open={dialogOpen} onClose={handleClose} onSave={handleSave} faq={editingFaq} />
    </>
  );
};
